import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, downloadUrl } from "@/lib/api";
import { PACKAGES, ENTITY_TYPES, gbp, fmtDate, entityLabel } from "@/lib/constants";
import { StatusBadge, EmptyState, Modal } from "@/components/ui/bits";
import DocumentUpload from "@/components/DocumentUpload";
import MessageThread from "@/components/MessageThread";
import {
  ArrowLeft, FileText, Download, Trash2, Plus, Clock, CheckCircle2, Circle, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const TABS = ["Overview", "Documents", "Messages", "Invoices", "Time", "Settings"];

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [deadlines, setDeadlines] = useState([]);
  const [docs, setDocs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [times, setTimes] = useState([]);
  const [infoReqs, setInfoReqs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [delConfirm, setDelConfirm] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [newReq, setNewReq] = useState({ title: "", description: "" });
  const [newInv, setNewInv] = useState({ amount: "", period: "", due_date: "", description: "" });
  const [showInv, setShowInv] = useState(false);
  const [newTime, setNewTime] = useState({ minutes: "", description: "" });

  const loadClient = useCallback(() => api.get(`/clients/${id}`).then(({ data }) => setClient(data)).catch((e) => { toast.error(apiError(e)); navigate("/clients"); }), [id, navigate]);
  const loadDocs = useCallback(() => api.get(`/documents?client_id=${id}`).then(({ data }) => setDocs(data)).catch(() => {}), [id]);
  const loadDeadlines = useCallback(() => api.get(`/deadlines?client_id=${id}`).then(({ data }) => setDeadlines(data)).catch(() => {}), [id]);
  const loadInvoices = useCallback(() => api.get(`/invoices?client_id=${id}`).then(({ data }) => setInvoices(data)).catch(() => {}), [id]);
  const loadTimes = useCallback(() => api.get(`/time-entries?client_id=${id}`).then(({ data }) => setTimes(data)).catch(() => {}), [id]);
  const loadReqs = useCallback(() => api.get(`/info-requests?client_id=${id}`).then(({ data }) => setInfoReqs(data)).catch(() => {}), [id]);

  useEffect(() => { loadClient(); loadDocs(); loadDeadlines(); loadInvoices(); loadTimes(); loadReqs(); }, [loadClient, loadDocs, loadDeadlines, loadInvoices, loadTimes, loadReqs]);
  useEffect(() => { if (user.role === "admin") api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {}); }, [user.role]);

  if (!client) return <div className="text-slate-400">Loading…</div>;

  const saveField = async (patch) => {
    try { const { data } = await api.put(`/clients/${id}`, patch); setClient(data); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleChecklist = async (key) => {
    const next = !client.checklist?.[key];
    try { const { data } = await api.put(`/clients/${id}/checklist`, { [key]: next }); setClient({ ...client, checklist: data }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const regenerate = async (earlier) => {
    try { const { data } = await api.post(`/clients/${id}/regenerate-deadlines?include_earlier=${earlier}`); toast.success(`${data.deadlines_created} deadlines regenerated`); loadDeadlines(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteDoc = async (docId) => { try { await api.delete(`/documents/${docId}`); loadDocs(); toast.success("Removed"); } catch (e) { toast.error(apiError(e)); } };
  const deleteClient = async () => {
    try { await api.delete(`/clients/${id}`); toast.success("Client deleted"); navigate("/clients"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const createReq = async (e) => { e.preventDefault(); try { await api.post(`/clients/${id}/info-requests`, newReq); setNewReq({ title: "", description: "" }); loadReqs(); toast.success("Request sent"); } catch (e) { toast.error(apiError(e)); } };
  const createInv = async (e) => { e.preventDefault(); try { await api.post(`/clients/${id}/invoices`, { ...newInv, amount: Number(newInv.amount) }); setShowInv(false); setNewInv({ amount: "", period: "", due_date: "", description: "" }); loadInvoices(); toast.success("Invoice created"); } catch (e) { toast.error(apiError(e)); } };
  const addTime = async (e) => { e.preventDefault(); try { await api.post(`/time-entries`, { client_id: id, minutes: Number(newTime.minutes), description: newTime.description }); setNewTime({ minutes: "", description: "" }); loadTimes(); toast.success("Logged"); } catch (e) { toast.error(apiError(e)); } };

  const checklistItems = [
    { key: "engagement_letter", label: "Engagement letter" },
    { key: "aml_id", label: "AML / ID verification" },
    { key: "esignature", label: "E-signature" },
  ];
  const field = "w-full bg-[#0A0F1F] border border-[#1E294B] rounded-xl px-4 py-3 text-white outline-none focus:border-[#C9A84A]/60";

  return (
    <div>
      <button onClick={() => navigate("/clients")} className="flex items-center gap-2 text-slate-400 hover:text-white mb-4"><ArrowLeft size={18} /> Clients</button>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif font-bold text-3xl text-white">{client.name}</h1>
          <p className="text-slate-400">{entityLabel(client.entity_type)} · {PACKAGES[client.package]?.name} · {gbp(client.fee)}/mo</p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto hide-scrollbar my-5">
        {TABS.filter((t) => t !== "Settings" || user.role === "admin").map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`clienttab-${t.toLowerCase()}`}
            className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm border transition-colors ${tab === t ? "bg-[#C9A84A] text-[#0A0F1F] border-[#C9A84A] font-semibold" : "card-surface text-slate-300 border-[#1E294B]"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="space-y-5">
          <div className="card-surface rounded-2xl p-5">
            <h3 className="font-serif font-bold text-lg text-white mb-3">Onboarding checklist</h3>
            <div className="space-y-2">
              {checklistItems.map((it) => (
                <button key={it.key} onClick={() => toggleChecklist(it.key)} data-testid={`checklist-${it.key}`}
                  className="w-full flex items-center gap-3 text-left py-1.5">
                  {client.checklist?.[it.key] ? <CheckCircle2 className="text-emerald-400" size={20} /> : <Circle className="text-slate-500" size={20} />}
                  <span className={client.checklist?.[it.key] ? "text-white" : "text-slate-400"}>{it.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Start date" value={fmtDate(client.start_date)} />
            <Info label="Year end" value={fmtDate(client.year_end)} />
            {client.company_number && <Info label="Company no." value={client.company_number} />}
            {client.incorporation_date && <Info label="Incorporated" value={fmtDate(client.incorporation_date)} />}
            <Info label="VAT" value={client.vat_registered ? `Yes (stagger ${client.vat_stagger})` : "No"} />
            <Info label="Self Assessment" value={client.self_assessment ? "Yes" : "No"} />
            {client.assigned_staff_name && <Info label="Managed by" value={client.assigned_staff_name} />}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif font-bold text-lg text-white">Deadlines</h3>
              <span className="text-slate-500 text-sm">{deadlines.length} total</span>
            </div>
            {deadlines.length === 0 ? <EmptyState icon={Clock} title="No deadlines" /> : (
              <div className="space-y-2">
                {deadlines.slice(0, 12).map((d) => (
                  <div key={d.id} className="card-surface rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <div className="text-white text-sm">{d.title}</div>
                      <div className="text-slate-500 text-xs">Due {fmtDate(d.due_date)}</div>
                    </div>
                    <StatusBadge status={d.status} overdue={d.is_overdue} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-surface rounded-2xl p-5">
            <h3 className="font-serif font-bold text-lg text-white mb-3">Information requests</h3>
            <form onSubmit={createReq} className="flex gap-2 mb-3">
              <input className={field} placeholder="What do you need from the client?" value={newReq.title} onChange={(e) => setNewReq({ ...newReq, title: e.target.value })} required data-testid="info-req-title" />
              <button className="bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-xl px-4"><Plus size={18} /></button>
            </form>
            {infoReqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2 border-t border-[#1E294B]">
                <span className="text-slate-200 text-sm">{r.title}</span>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "Documents" && (
        <div className="space-y-5">
          <DocumentUpload clientId={id} category="report" onUploaded={loadDocs} />
          <p className="text-slate-500 text-sm">Documents you upload here are shared with the client (including reports).</p>
          <DocList docs={docs} onDelete={deleteDoc} />
        </div>
      )}

      {tab === "Messages" && <MessageThread clientId={id} />}

      {tab === "Invoices" && (
        <div className="space-y-4">
          {user.role === "admin" && <button onClick={() => setShowInv(true)} className="bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-full px-5 py-2.5 flex items-center gap-2" data-testid="new-invoice"><Plus size={16} /> New invoice</button>}
          {invoices.length === 0 ? <EmptyState icon={FileText} title="No invoices" /> : invoices.map((inv) => (
            <div key={inv.id} className="card-surface rounded-xl p-4 flex items-center justify-between">
              <div><div className="text-white">{inv.number} · {gbp(inv.amount)}</div><div className="text-slate-500 text-sm">{inv.period} · due {fmtDate(inv.due_date)}</div></div>
              {user.role === "admin" ? (
                <select value={inv.status} onChange={async (e) => { await api.put(`/invoices/${inv.id}/status?status=${e.target.value}`); loadInvoices(); }} className="bg-[#0A0F1F] border border-[#1E294B] rounded-lg px-2 py-1.5 text-sm text-white">
                  <option value="due">Due</option><option value="paid">Paid</option>
                </select>
              ) : <StatusBadge status={inv.status} />}
            </div>
          ))}
        </div>
      )}

      {tab === "Time" && (
        <div className="space-y-4">
          <form onSubmit={addTime} className="card-surface rounded-2xl p-4 grid grid-cols-[100px_1fr_auto] gap-2 items-end">
            <div><label className="text-slate-400 text-xs">Minutes</label><input type="number" className={field} value={newTime.minutes} onChange={(e) => setNewTime({ ...newTime, minutes: e.target.value })} required /></div>
            <div><label className="text-slate-400 text-xs">Description</label><input className={field} value={newTime.description} onChange={(e) => setNewTime({ ...newTime, description: e.target.value })} required /></div>
            <button className="bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-xl px-4 py-3">Log</button>
          </form>
          <p className="text-slate-400 text-sm">Total logged: <span className="text-white font-medium">{(times.reduce((s, t) => s + t.minutes, 0) / 60).toFixed(1)}h</span> · outside-package work billed at £35/hour</p>
          {times.map((t) => (
            <div key={t.id} className="card-surface rounded-xl p-3 flex items-center justify-between">
              <div><div className="text-white text-sm">{t.description}</div><div className="text-slate-500 text-xs">{t.staff_name} · {fmtDate(t.date)}</div></div>
              <span className="text-slate-300">{(t.minutes / 60).toFixed(1)}h</span>
            </div>
          ))}
        </div>
      )}

      {tab === "Settings" && user.role === "admin" && (
        <div className="space-y-5">
          <div className="card-surface rounded-2xl p-5 space-y-4">
            <h3 className="font-serif font-bold text-lg text-white">Edit client</h3>
            <div><label className="text-slate-400 text-sm">Package</label>
              <select className={field} value={client.package} onChange={(e) => saveField({ package: e.target.value })}>
                {Object.entries(PACKAGES).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
              </select>
            </div>
            <div><label className="text-slate-400 text-sm">Monthly fee (£)</label>
              <input type="number" className={field} defaultValue={client.fee} onBlur={(e) => saveField({ fee: Number(e.target.value) })} />
            </div>
            <div><label className="text-slate-400 text-sm">Assigned staff</label>
              <select className={field} value={client.assigned_staff_id || ""} onChange={(e) => saveField({ assigned_staff_id: e.target.value || null })}>
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <div className="card-surface rounded-2xl p-5">
            <h3 className="font-serif font-bold text-lg text-white mb-2">Deadlines</h3>
            <div className="flex gap-3 flex-wrap">
              <button onClick={() => regenerate(false)} className="card-surface border border-[#1E294B] rounded-full px-4 py-2 text-white text-sm hover:bg-[#18243F]">Regenerate</button>
              <button onClick={() => regenerate(true)} className="card-surface border border-[#1E294B] rounded-full px-4 py-2 text-white text-sm hover:bg-[#18243F]">Regenerate incl. earlier</button>
            </div>
            <p className="text-slate-500 text-xs mt-2">Completed deadlines are preserved.</p>
          </div>

          <div className="rounded-2xl p-5 border border-red-500/30 bg-red-500/5">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="text-red-400" size={20} /><h3 className="font-serif font-bold text-lg text-white">Danger zone</h3></div>
            <p className="text-slate-400 text-sm mb-3">Permanently delete this client and all associated data (deadlines, documents, messages, invoices).</p>
            <button onClick={() => setShowDelete(true)} className="text-red-300 border border-red-500/40 rounded-full px-5 py-2.5 hover:bg-red-500/10" data-testid="delete-client"><Trash2 size={16} className="inline mr-2" />Delete client</button>
          </div>
        </div>
      )}

      <Modal open={showInv} onClose={() => setShowInv(false)} title="New invoice">
        <form onSubmit={createInv} className="space-y-3">
          <input type="number" className={field} placeholder="Amount (£)" value={newInv.amount} onChange={(e) => setNewInv({ ...newInv, amount: e.target.value })} required />
          <input className={field} placeholder="Period (e.g. March 2026)" value={newInv.period} onChange={(e) => setNewInv({ ...newInv, period: e.target.value })} required />
          <div><label className="text-slate-400 text-sm">Due date</label><input type="date" className={field} value={newInv.due_date} onChange={(e) => setNewInv({ ...newInv, due_date: e.target.value })} required /></div>
          <input className={field} placeholder="Description (optional)" value={newInv.description} onChange={(e) => setNewInv({ ...newInv, description: e.target.value })} />
          <button className="w-full bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-xl py-3">Create invoice</button>
        </form>
      </Modal>

      <Modal open={showDelete} onClose={() => { setShowDelete(false); setDelConfirm(""); }} title="Delete client?">
        <p className="text-slate-400 mb-4">This cannot be undone. Type <span className="text-white font-semibold">{client.name}</span> to confirm.</p>
        <input className={field} value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder={client.name} data-testid="delete-confirm-input" />
        <div className="flex gap-3 justify-end mt-4">
          <button onClick={() => { setShowDelete(false); setDelConfirm(""); }} className="px-4 py-2 text-slate-300">Cancel</button>
          <button disabled={delConfirm !== client.name} onClick={deleteClient} data-testid="confirm-delete-client"
            className="bg-red-500 hover:bg-red-600 text-white font-bold rounded-full px-5 py-2 disabled:opacity-40">Delete permanently</button>
        </div>
      </Modal>
    </div>
  );
}

function Info({ label, value }) {
  return <div className="card-surface rounded-xl p-3"><div className="text-slate-500 text-xs">{label}</div><div className="text-white">{value}</div></div>;
}

function DocList({ docs, onDelete }) {
  if (docs.length === 0) return <EmptyState icon={FileText} title="No documents yet" />;
  return (
    <div className="space-y-2">
      {docs.map((d) => (
        <div key={d.id} className="card-surface rounded-xl p-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-3">
            <FileText className="text-[#C9A84A] shrink-0" size={20} />
            <div className="min-w-0">
              <div className="text-white text-sm truncate">{d.original_filename}</div>
              <div className="text-slate-500 text-xs">{(d.size / 1024).toFixed(0)} KB · {d.uploaded_by_name} ({d.uploaded_by_role}){d.category === "report" ? " · report" : ""}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a href={downloadUrl(`documents/${d.id}/download`)} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white p-2"><Download size={18} /></a>
            <button onClick={() => onDelete(d.id)} className="text-slate-400 hover:text-red-400 p-2"><Trash2 size={18} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}
